import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { authApi, otpApi } from "@/api/endpoints/auth";
import { clearSurfaceHint, seedSurfaceHint } from "@/features/capture/use-capture-me";
import { setSessionToken, API_ORIGIN } from "@/api/client";
import { getDeviceId } from "@/shared/lib/device-id";
import { identifyUser, resetUser, trackEvent } from "@/shared/lib/posthog";
import type {
  AuthData,
  AuthContextType,
  OtpChallenge,
  OtpRequestInput,
  OtpVerifyInput,
} from "../types";

const STORAGE_KEY = "ubik_session_token";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Extract and consume the `?session=` query parameter from the URL.
 * This is set by the backend after OAuth (Google and Microsoft flows).
 * Returns the token and cleans up the URL so the token doesn't linger.
 */
function consumeSessionFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("session");

  if (token) {
    params.delete("session");
    const cleanUrl =
      params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  }

  return token;
}

/**
 * Persist the session token in localStorage so it survives page reloads
 * and browser restarts (matching the 30-day server-side session expiry).
 */
function persistToken(token: string | null) {
  if (token) {
    localStorage.setItem(STORAGE_KEY, token);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  setSessionToken(token);
}

function loadPersistedToken(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authData, setAuthData] = useState<AuthData | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionUnreachable, setSessionUnreachable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const validatingRef = useRef(false);

  /**
   * Validate the session on mount.
   *
   * Priority:
   * 1. `?session=` query param (fresh OAuth redirect)
   * 2. Token persisted in sessionStorage (page reload)
   * 3. httpOnly cookie (fallback, works when backend is same-origin)
   */
  const validateSession = useCallback(async (retryOnNetworkError = true) => {
    if (validatingRef.current) return;
    validatingRef.current = true;
    let retryScheduled = false;

    try {
      const urlToken = consumeSessionFromUrl();
      const token = urlToken ?? loadPersistedToken();

      console.log("[Auth] URL token found:", !!urlToken, "| Persisted token:", !!loadPersistedToken());

      if (token) {
        setSessionToken(token);
      }

      const sessionInfo = await authApi.getSession(token ?? undefined);

      if (sessionInfo) {
        const { userProfile, expiresAt } = sessionInfo;
        setSessionUnreachable(false);

        console.log("[Auth] Session result: valid user");

        // If we authenticated via cookie only (no token in URL or localStorage),
        // fall back to the session token returned by the server so the email
        // backend's X-Session-Token header is populated for subsequent requests.
        const resolvedToken = token ?? sessionInfo.sessionToken ?? null;
        persistToken(resolvedToken);
        setAuthData({ userProfile });
        setSessionExpiresAt(expiresAt);
        setError(null);

        // Everything past this point is a best-effort side effect of a session
        // that has ALREADY validated. A throw here would otherwise land in the
        // catch below, which reads any throw as a network verdict and (on the
        // second attempt) signs the user out — so a PostHog hiccup or a
        // WebView with storage blocked would log out a perfectly valid user.
        // Only a definitive 401 (the `else` branch) may clear the session.
        try {
          identifyUser({
            id: userProfile.id,
            email: userProfile.email,
            name: userProfile.name,
            orgId: userProfile.orgId,
            orgRole: userProfile.orgRole,
          });
          trackEvent("session_validated", {
            provider: urlToken ? "oauth_redirect" : "persisted",
          });
          if (urlToken) {
            trackEvent("user_signed_in");
          }

          // Sync session token to Electron overlay if it's running.
          // The overlay's transcriptStream needs a valid token to send meeting events.
          if (token) {
            const handoffPort = import.meta.env.VITE_UBIK_HANDOFF_PORT ?? "9001";
            // Pass the browser's device id so the overlay can register this
            // device with the backend (the per-device install signal). The
            // desktop does the recording; success/failure here is best-effort.
            fetch(`http://127.0.0.1:${handoffPort}/set-session`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token, device_id: getDeviceId() }),
              signal: AbortSignal.timeout(1500),
            }).catch(() => {}); // Electron not running — ignore silently
          }
        } catch (sideEffectErr) {
          console.warn("[Auth] Post-validation side effect failed:", sideEffectErr);
        }
      } else {
        console.log("[Auth] Session result: null (401 or no token)");
        setSessionUnreachable(false);
        persistToken(null);
        setAuthData(null);
        setSessionExpiresAt(null);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "TimeoutError") {
        console.warn("[Auth] Session check timed out — backend unreachable");
      } else {
        console.error("[Auth] Session validation failed:", err);
      }
      // A THROWN fetch is a network verdict, not an auth verdict — offline
      // boot, DNS blip, or the app-resume race right after the login Custom
      // Tab closes. Wiping the token here produced a live login loop on the
      // pilot device (sign in → blip → token wiped → sign in again), and on
      // a factory floor an offline boot would silently log the operator out.
      // Only a definitive 401 (the `else` branch above) clears the token.
      if (retryOnNetworkError) {
        // One quiet retry, spinner kept up — not a logout flash.
        retryScheduled = true;
        validatingRef.current = false;
        setTimeout(() => {
          void validateSession(false);
        }, 2000);
      } else {
        // Out of retries. If a token is still stored, this is "cannot reach
        // Ubik", NOT "signed out" — say so, so the UI can offer a retry
        // instead of a login form the operator does not need.
        setAuthData(null);
        setSessionExpiresAt(null);
        setSessionUnreachable(Boolean(loadPersistedToken()));
      }
    } finally {
      if (!retryScheduled) {
        setLoading(false);
        validatingRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Re-validate the session when the tab regains focus after being hidden.
  // Catches the "laptop slept for two weeks" case — if the session was revoked
  // server-side (or expired), we log the user out instead of letting them poke
  // around an authenticated UI that 401s on every action.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        validateSession();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [validateSession]);

  const startGoogleAuth = useCallback(() => {
    setError(null);
    // Inside the Capacitor shell the WebView must NOT navigate to Google —
    // OAuth in embedded WebViews is blocked (disallowed_useragent). The shell
    // opens the ordinary web login in a Chrome Custom Tab instead; the token
    // comes back through the ubikcapture:// deep link (see native-bridge.ts).
    if (window.Capacitor?.isNativePlatform?.()) {
      void import("@/features/capture/native-bridge").then((bridge) =>
        bridge.openNativeLogin(API_ORIGIN, "google"),
      );
      return;
    }
    window.location.href = authApi.getGoogleLoginUrl();
  }, []);

  const startAppleAuth = useCallback(() => {
    setError(null);
    // Same rule as Google: the shell opens the web login in the browser sheet
    // and the token comes back through the deep link.
    if (window.Capacitor?.isNativePlatform?.()) {
      void import("@/features/capture/native-bridge").then((bridge) =>
        bridge.openNativeLogin(API_ORIGIN, "apple"),
      );
      return;
    }
    window.location.href = authApi.getAppleLoginUrl();
  }, []);

  const startMicrosoftAuth = useCallback(async () => {
    setError(null);
    // Same rule as Google: never run OAuth inside the WebView. Before this the
    // Microsoft button navigated the shell's WebView to login.microsoftonline
    // and the callback landed on the web origin INSIDE the app — a dead end.
    if (window.Capacitor?.isNativePlatform?.()) {
      void import("@/features/capture/native-bridge").then((bridge) =>
        bridge.openNativeLogin(API_ORIGIN, "microsoft"),
      );
      return;
    }
    try {
      const authUrl = await authApi.getMicrosoftLoginUrl();
      window.location.href = authUrl;
    } catch (err) {
      setError("Failed to start Microsoft sign-in. Please ensure the backend is running.");
      trackEvent("login_start_failed", { provider: "microsoft", reason: String(err) });
      console.error("[Auth] Microsoft auth error:", err);
    }
  }, []);

  /**
   * Ask the backend to send a one-time code — and to RESEND one: the backend
   * has a single endpoint for both. Nothing is persisted here; a challenge is
   * not a session.
   *
   * `input.phone` is a full E.164 number, which is PII. It goes to the API and
   * nowhere else: no console line, no PostHog property, no Sentry breadcrumb.
   * The only number this app ever renders is the backend's `masked_phone`.
   */
  const requestOtp = useCallback(async (input: OtpRequestInput): Promise<OtpChallenge> => {
    setError(null);
    const challenge = await otpApi.request(input);
    trackEvent("otp_code_requested", {
      // Country and the DELIVERED channel only — never the number.
      country: input.country,
      channel: challenge?.channel ?? null,
    });
    return challenge;
  }, []);

  /**
   * Exchange a code for a session. On success this does NOT return: it
   * hard-navigates to "/", exactly like the OAuth callback does, so
   * AuthContext re-validates the fresh token from the top rather than trying
   * to splice a user into a provider that is already mounted.
   *
   * The order of the four steps matters:
   *   1. persistToken   — same helper OAuth uses (localStorage + the header).
   *   2. mirrorNativeToken — the Capacitor Preferences half. PAIRED WITH
   *      clearNativeToken() in logout(); without it a floor phone that has its
   *      WebView data cleared is signed out for good.
   *   3. seedSurfaceHint — see use-capture-me.ts. First OTP sign-in is the one
   *      path where the capture hint is guaranteed absent, so skipping this
   *      makes a fresh handset download the whole workbench chunk.
   *   4. location.replace — replace, not assign: the login screen must not be
   *      reachable with the phone's Back button once a session exists.
   */
  const verifyOtp = useCallback(async (input: OtpVerifyInput): Promise<void> => {
    setError(null);
    const result = await otpApi.verify(input);

    // strictNullChecks is OFF in this project, so nothing above would have
    // flagged a missing token or user block. Check by hand.
    const token = result?.session_token;
    if (!token) {
      throw new Error("Sign-in succeeded but no session was returned. Try again.");
    }
    persistToken(token);

    await import("@/features/capture/native-bridge")
      .then((bridge) => bridge.mirrorNativeToken(token))
      .catch(() => {});

    seedSurfaceHint(result?.app_surface ?? null);

    // Best-effort analytics, after the session is already safe. A phone-only
    // user has no email and may have no name — identifyUser tolerates nulls,
    // and neither the number nor the masked number is ever sent.
    try {
      const user = result?.user;
      if (user?.id) {
        identifyUser({ id: user.id, email: user.email ?? null, name: user.name ?? null });
      }
      trackEvent("user_signed_in", { provider: "phone_otp" });
    } catch (sideEffectErr) {
      console.warn("[Auth] Post-OTP side effect failed:", sideEffectErr);
    }

    window.location.replace("/");
  }, []);

  /**
   * Every local trace of the session: the token, the Capacitor Preferences
   * mirror, cached meetings, the surface hint and analytics identity. Shared by
   * logout() and deleteAccount() so the two can never drift apart — a deleted
   * account that left its token mirrored would "restore" on the next launch
   * into a 401 loop.
   */
  const clearLocalSession = useCallback(async () => {
    persistToken(null);
    // Same shared-device reasoning as the native-token clear below: the
    // meetings list is cached in localStorage to avoid a spinner on every
    // visit, and persistToken() does not touch it. Without this, the next
    // person to sign in on this browser is handed the previous user's
    // meetings as initial data.
    void import("@/features/meetings/lib/meetings-cache")
      .then((cache) => cache.clearAllCachedMeetings())
      .catch(() => {});
    setAuthData(null);
    setSessionExpiresAt(null);
    setError(null);
    resetUser();
    // The SurfaceGate hint is per-DEVICE, not per-session, and nothing else
    // clears it: seedSurfaceHint only clears on a known non-capture surface,
    // rememberSurface only after /capture/me answers. On a shared handset
    // that left an operator's "capture" behind for the next person to sign
    // in — straight into the capture shell. Same hazard as the token mirror
    // below, same place to fix it.
    clearSurfaceHint();
    // persistToken() only clears localStorage. The Capacitor Preferences
    // mirror outlives it, and restoreToken() re-seeds from it on the next
    // launch — so without this a shared floor phone reopens signed in as the
    // operator who just signed out. No-ops off the native shell.
    await import("@/features/capture/native-bridge")
      .then((bridge) => bridge.clearNativeToken())
      .catch(() => {});
  }, []);

  const logout = useCallback(async () => {
    try {
      const token = loadPersistedToken();
      await authApi.logout(token ?? undefined);
    } catch (err) {
      console.error("[Auth] Logout error:", err);
    } finally {
      trackEvent("user_signed_out");
      await clearLocalSession();
    }
  }, [clearLocalSession]);

  /**
   * Unlike logout(), a failure here must NOT clear anything: if the backend
   * refused (last admin of a workspace) the account still exists and the
   * person is still signed in. Only after a 2xx is the local session wiped.
   * replace(), not assign(): Back must not return to a signed-in screen.
   */
  const deleteAccount = useCallback(async () => {
    const token = loadPersistedToken();
    await authApi.deleteAccount(token ?? undefined);
    trackEvent("account_deleted");
    await clearLocalSession();
    window.location.replace("/login");
  }, [clearLocalSession]);

  const value: AuthContextType = {
    authData,
    sessionExpiresAt,
    loading,
    error,
    startGoogleAuth,
    startMicrosoftAuth,
    startAppleAuth,
    logout,
    deleteAccount,
    refreshSession: validateSession,
    requestOtp,
    verifyOtp,
    sessionUnreachable,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Safe variant — returns null when called outside an AuthProvider (e.g. mock mode
// where AppRoutes renders without the provider). Use this in pages that need the
// user's org/email but must also work in mock/unauthenticated contexts.
export const useAuthSafe = (): AuthContextType | null => {
  return useContext(AuthContext) ?? null;
};
